// NearMissPharmacyDialog.js
// -----------------------------------------------------------------------------
// THE near-miss drill-down for ONE pharmacy — a large tabbed dialog opened
// from every place a pharmacy's near-miss detail is reached:
//   1. Site analysis (NearMissAnalysis branch click)
//   2. Head Office → Safety → Near miss (full-page NearMissAnalysis)
//   3. Single pharmacy view → Near miss panel
//   4. Regulatory Compliance → branch drill-down → near-miss element
//
// Tabs:
//   • Current month  — 1st of this month → today, labelled with the exact
//                      range. Left third: latest engine insights. Centre: pie
//                      of incidents by category. Right: scrollable incident list.
//   • Monthly view   — same 3-panel layout for a chosen month (dropdown,
//                      default = last full month). Left panel adds the
//                      engine's monthly summary AND the manager's monthly
//                      review where one exists. Export to PDF / Excel.
//   • All monthly summaries — scrollable grid of every engine summary +
//                      the latest insights (the previous popup's content).
//   • Trends         — bar chart of incidents by month; year selector,
//                      default last 12 months.
//
// Data sources (all existing endpoints, plus the range-aware
// single-extras/nearmiss added alongside this component):
//   POST /near_miss_insights            { pharmacyName }
//   POST /near_miss_monthly_summaries   { pharmacyName, limit }
//   POST /near_miss_monthly_report      { pharmacyName }   (manager reviews)
//   POST /near_miss_analysis_trend      { pharmacyId, months }
//   GET  /api/headoffice/single-extras/nearmiss?pharmacyName=&from=&to=
// -----------------------------------------------------------------------------
import React, { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";

const ROOT_BASE = "https://api.pharmacegroup.co.uk";
const API_BASE = "https://api.pharmacegroup.co.uk";

const MONTH_NAMES = ["January","February","March","April","May","June",
                     "July","August","September","October","November","December"];
const PIE_PALETTE = ["#3b82f6", "#ef4444", "#f59e0b", "#10b981", "#8b5cf6",
                     "#ec4899", "#14b8a6", "#f97316", "#6366f1", "#9ca3af"];

const fmtD = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");
const fmtDT = (d) => (d ? new Date(d).toLocaleString("en-GB",
  { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

async function rootPost(path, token, body) {
  const res = await fetch(`${ROOT_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body || {})
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
async function apiGetJson(path, token) {
  const res = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// -----------------------------------------------------------------------------
// Small shared pieces
// -----------------------------------------------------------------------------
function PanelBox({ title, right, children, style }) {
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden",
                  display: "flex", flexDirection: "column", minHeight: 0, ...style }}>
      <div style={{ padding: "8px 10px", borderBottom: "1px solid #eee", background: "#f9fafb",
                    fontWeight: 600, fontSize: 13, display: "flex",
                    justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span>{title}</span>{right || null}
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 10, minHeight: 0 }}>{children}</div>
    </div>
  );
}

function InsightItem({ ins }) {
  const colorMap = { "I-1": "#fbbf24", "I-2": "#60a5fa", "I-3": "#a78bfa", "I-4": "#f87171",
                     "I-5": "#34d399", "I-6": "#9ca3af", "I-7": "#ef4444", "I-8": "#9ca3af", "I-9": "#fb923c" };
  return (
    <div style={{ padding: "8px 10px", marginBottom: 6, border: "1px solid #e5e7eb",
                  borderLeft: `4px solid ${colorMap[ins.id] || "#6b7280"}`, borderRadius: 4, background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 3 }}>
        <span style={{ fontWeight: 600, fontSize: 12.5 }}>{ins.title}</span>
        <span style={{ fontSize: 10, color: "#9ca3af" }}>{ins.id}</span>
      </div>
      <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.5 }}>{ins.detail}</div>
    </div>
  );
}

function CategoryPie({ categories, title }) {
  const data = (categories || []).map((c, i) => ({
    name: c.errorTypeId || "(uncategorised)", value: c.count,
    itemStyle: { color: PIE_PALETTE[i % PIE_PALETTE.length] }
  }));
  if (!data.length) return <div style={{ fontSize: 12, color: "#6b7280", padding: 12 }}>No incidents in this period.</div>;
  const option = {
    title: { text: title, left: "center", top: 4, textStyle: { fontSize: 13, fontWeight: 600 } },
    tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
    series: [{ type: "pie", radius: ["30%", "62%"], center: ["50%", "56%"],
               label: { fontSize: 10, overflow: "truncate", width: 90 }, data }]
  };
  return <ReactECharts option={option} style={{ height: "100%", minHeight: 260, width: "100%" }} notMerge />;
}

function IncidentList({ incidents, emptyText }) {
  // Every incident renders fully expanded — full description plus every
  // recorded field — so the list is readable without any clicking.
  if (!incidents || incidents.length === 0)
    return <div style={{ fontSize: 12, color: "#6b7280" }}>{emptyText || "No incidents in this period."}</div>;
  return (
    <div>
      {incidents.map((r, i) => {
        const HIDDEN = ["timestamp", "createdAt", "updatedAt", "errorTypeId", "description",
                        "pharmacyId", "pharmacyUid", "dbName", "organisationId"];
        const extra = Object.entries(r.detail || {})
          .filter(([k, v]) => v != null && typeof v !== "object" &&
                              !HIDDEN.includes(k) &&
                              // raw Mongo ids (e.g. *StaffId before the backend
                              // name-resolution deploy) are meaningless — hide them
                              !(/id$/i.test(k) && /^[0-9a-fA-F]{24}$/.test(String(v))));
        return (
          <div key={i}
               style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "7px 9px",
                        marginBottom: 6, background: "#fff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#991b1b" }}>{r.errorTypeId || "—"}</span>
              <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>{fmtD(r.date)}</span>
            </div>
            {r.description && (
              <div style={{ fontSize: 12, color: "#374151", marginTop: 3, lineHeight: 1.4 }}>
                {r.description}
              </div>
            )}
            {extra.length > 0 && (
              <table style={{ marginTop: 6, fontSize: 11, color: "#4b5563", borderCollapse: "collapse" }}>
                <tbody>
                  {extra.map(([k, v]) => (
                    <tr key={k}>
                      <td style={{ paddingRight: 8, fontWeight: 600, verticalAlign: "top", whiteSpace: "nowrap" }}>
                        {k.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase())}
                      </td>
                      <td>{String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

// The three-panel layout shared by "Current month" and "Monthly view".
function ThreePanel({ left, categories, pieTitle, incidents }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, flex: 1, minHeight: 0 }}>
      {left}
      <PanelBox title="Near miss by category"><CategoryPie categories={categories} title={pieTitle} /></PanelBox>
      <PanelBox title={`Near misses (${(incidents || []).length})`}>
        <IncidentList incidents={incidents} />
      </PanelBox>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Exports (Monthly view)
// -----------------------------------------------------------------------------
function csvCell(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportMonthExcel({ pharmacyName, monthLabel, summary, incidents,
                            categories, insights, managerReviews }) {
  const lines = [];
  lines.push(["Pharmacy", pharmacyName].map(csvCell).join(","));
  lines.push(["Month", monthLabel].map(csvCell).join(","));
  if (summary) {
    lines.push(["Total incidents", summary.totalIncidents ?? incidents.length].map(csvCell).join(","));
    if (summary.narrative) lines.push(["Engine summary", summary.narrative].map(csvCell).join(","));
  } else {
    lines.push(["Total incidents", incidents.length].map(csvCell).join(","));
  }

  // --- Left panel: manager monthly review(s) ---
  for (const r of (managerReviews || [])) {
    lines.push("");
    lines.push(csvCell("MANAGER MONTHLY REVIEW" + (r.createdAt ? ` (${fmtD(r.createdAt)})` : "")));
    for (const [k, v] of Object.entries(r)) {
      if (v == null || typeof v === "object" || ["_id", "isDeleted", "__v"].includes(k)) continue;
      lines.push([k.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase()), v].map(csvCell).join(","));
    }
    const staff = Array.isArray(r.otherStaffNames) ? r.otherStaffNames.filter(Boolean).join(", ") : null;
    if (staff) lines.push(["Other staff", staff].map(csvCell).join(","));
  }

  // --- Left panel: patterns / insights ---
  if (insights && insights.length) {
    lines.push("");
    lines.push(csvCell("PATTERNS / INSIGHTS"));
    lines.push(["Id", "Title", "Detail"].map(csvCell).join(","));
    for (const i of insights) lines.push([i.id, i.title, i.detail].map(csvCell).join(","));
  }

  // --- Centre panel: near miss by category ---
  if (categories && categories.length) {
    const catTotal = categories.reduce((a, c) => a + (c.count || 0), 0) || 1;
    lines.push("");
    lines.push(csvCell("NEAR MISS BY CATEGORY"));
    lines.push(["Category", "Count", "% of month"].map(csvCell).join(","));
    for (const c of categories) {
      lines.push([c.errorTypeId || "(uncategorised)", c.count,
                  `${((c.count / catTotal) * 100).toFixed(1)}%`].map(csvCell).join(","));
    }
  }

  // --- Right panel: the incidents themselves ---
  lines.push("");
  lines.push(csvCell("INCIDENTS"));
  // Union of primitive fields across the incidents so nothing recorded is lost.
  const keys = ["date", "errorTypeId", "description"];
  const extraKeys = new Set();
  const HIDDEN = ["timestamp", "createdAt", "updatedAt", "errorTypeId", "description",
                  "pharmacyId", "pharmacyUid", "dbName", "organisationId"];
  for (const r of incidents) {
    for (const [k, v] of Object.entries(r.detail || {})) {
      if (v != null && typeof v !== "object" && !HIDDEN.includes(k) &&
          !(/id$/i.test(k) && /^[0-9a-fA-F]{24}$/.test(String(v)))) extraKeys.add(k);
    }
  }
  const allKeys = [...keys, ...extraKeys];
  lines.push(allKeys.map(csvCell).join(","));
  for (const r of incidents) {
    lines.push(allKeys.map(k => {
      if (k === "date") return csvCell(fmtDT(r.date));
      if (k === "errorTypeId") return csvCell(r.errorTypeId);
      if (k === "description") return csvCell(r.description);
      return csvCell(r.detail ? r.detail[k] : "");
    }).join(","));
  }
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `near-miss-${pharmacyName.replace(/[^\w-]+/g, "_")}-${monthLabel.replace(/\s+/g, "-")}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function exportMonthPdf({ pharmacyName, monthLabel, rangeText, summary, managerReviews, insights, incidents, categories }) {
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const insHtml = (insights || []).map(i =>
    `<div class="ins"><b>${esc(i.title)}</b> <span class="mut">(${esc(i.id)})</span><br>${esc(i.detail)}</div>`).join("");
  const revHtml = (managerReviews || []).map(r => {
    const rows = Object.entries(r)
      .filter(([k, v]) => v != null && typeof v !== "object" && !["_id", "isDeleted", "__v"].includes(k))
      .map(([k, v]) => `<tr><td class="k">${esc(k)}</td><td>${esc(v)}</td></tr>`).join("");
    return `<table class="rev">${rows}</table>`;
  }).join("");
  const catTotal = (categories || []).reduce((a, c) => a + (c.count || 0), 0) || 1;
  const catHtml = (categories || []).map(c => {
    const pct = ((c.count || 0) / catTotal) * 100;
    return `<tr><td>${esc(c.errorTypeId || "(uncategorised)")}</td>
      <td style="text-align:right">${esc(c.count)}</td>
      <td style="text-align:right">${pct.toFixed(1)}%</td>
      <td style="width:40%"><div style="background:#3b82f6;height:10px;width:${Math.max(2, pct)}%"></div></td></tr>`;
  }).join("");
  const HIDDEN_INC = ["timestamp", "createdAt", "updatedAt", "errorTypeId", "description",
                      "pharmacyId", "pharmacyUid", "dbName", "organisationId"];
  const incHtml = (incidents || []).map(r => {
    const extra = Object.entries(r.detail || {})
      .filter(([k, v]) => v != null && typeof v !== "object" && !HIDDEN_INC.includes(k) &&
                          !(/id$/i.test(k) && /^[0-9a-fA-F]{24}$/.test(String(v))))
      .map(([k, v]) => `<b>${esc(k.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase()))}:</b> ${esc(v)}`)
      .join("<br>");
    return `<tr><td style="white-space:nowrap">${esc(fmtDT(r.date))}</td><td>${esc(r.errorTypeId || "")}</td><td>${esc(r.description || "")}${extra ? "<br>" + extra : ""}</td></tr>`;
  }).join("");
  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) { window.alert("Pop-up blocked — allow pop-ups to export a PDF."); return; }
  w.document.write(`<!doctype html><html><head><title>Near miss — ${esc(pharmacyName)} — ${esc(monthLabel)}</title>
    <style>
      body{font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#111;margin:24px}
      h1{font-size:18px;margin:0 0 2px} h2{font-size:14px;margin:18px 0 6px} .mut{color:#6b7280}
      .ins{border-left:3px solid #f59e0b;padding:6px 8px;margin:6px 0;background:#fffbeb}
      table{border-collapse:collapse;width:100%} td,th{border:1px solid #d1d5db;padding:4px 6px;text-align:left;vertical-align:top}
      .rev{margin:6px 0} .rev .k{font-weight:700;width:180px;background:#f9fafb}
      .sum{background:#fef3c7;border:1px solid #fbbf24;border-radius:6px;padding:8px;margin:6px 0}
      @media print {.noprint{display:none}}
    </style></head><body>
    <button class="noprint" onclick="window.print()">Print / Save as PDF</button>
    <h1>Near miss report — ${esc(pharmacyName)}</h1>
    <div class="mut">${esc(monthLabel)} (${esc(rangeText)}) · generated ${esc(new Date().toLocaleString("en-GB"))}</div>
    ${summary ? `<h2>Engine monthly summary</h2><div class="sum"><b>${esc(summary.totalIncidents)} incident(s)</b> — ${esc(summary.narrative || "")}</div>` : ""}
    ${revHtml ? `<h2>Manager monthly review</h2>${revHtml}` : ""}
    ${insHtml ? `<h2>Patterns / insights</h2>${insHtml}` : ""}
    ${catHtml ? `<h2>Near miss by category</h2>
      <table><tr><th>Category</th><th style="text-align:right">Count</th><th style="text-align:right">%</th><th></th></tr>${catHtml}</table>` : ""}
    <h2>Incidents (${(incidents || []).length})</h2>
    <table><tr><th>Date</th><th>Error type</th><th>Description</th></tr>${incHtml || "<tr><td colspan=3>None recorded</td></tr>"}</table>
    </body></html>`);
  w.document.close();
}

// -----------------------------------------------------------------------------
// The dialog
// -----------------------------------------------------------------------------
export default function NearMissPharmacyDialog({ pharmacyName, pharmacyId = null, onClose, initialTab = "current" }) {
  const token = localStorage.getItem("authToken");
  const [tab, setTab] = useState(initialTab); // current | monthly | summaries | trends

  // Shared data (fetched once)
  const [insightsDoc, setInsightsDoc] = useState(null); // latest engine insights (90-day window)
  const [summaries, setSummaries] = useState([]);       // engine monthly summaries, newest first
  const [reports, setReports] = useState([]);           // manager monthly reviews
  const [baseLoading, setBaseLoading] = useState(true);

  // Tab 1 — current month incidents
  const now = new Date();
  const curFrom = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const [curData, setCurData] = useState(null);
  const [curLoading, setCurLoading] = useState(true);

  // Tab 2 — selected month (default: last full month)
  const lastFull = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const [selYear, setSelYear] = useState(lastFull.getUTCFullYear());
  const [selMonth, setSelMonth] = useState(lastFull.getUTCMonth() + 1); // 1-12
  const [monData, setMonData] = useState(null);
  const [monLoading, setMonLoading] = useState(false);

  // Tab 4 — trend
  const [trendSeries, setTrendSeries] = useState(null);
  const [trendYear, setTrendYear] = useState("last12"); // "last12" | a year number

  // Some entry points (the main Mockupp grid) pass the tenant dbName in the
  // pharmacyId slot, not a Mongo ObjectId — so the name is ALWAYS sent as the
  // reliable key, and pharmacyId only when it's genuinely id-shaped. The
  // backend tries id → name → dbName in that order.
  const idLooksReal = pharmacyId && /^[0-9a-fA-F]{24}$/.test(String(pharmacyId));
  const incidentsUrl = (from, to) => {
    const q = new URLSearchParams();
    q.set("pharmacyName", pharmacyName);
    if (pharmacyId) q.set("pharmacyId", String(pharmacyId));
    q.set("from", from.toISOString());
    q.set("to", to.toISOString());
    q.set("limit", "500");
    return `/api/headoffice/single-extras/nearmiss?${q.toString()}`;
  };

  // Base fetches
  useEffect(() => {
    let cancelled = false;
    setBaseLoading(true);
    Promise.allSettled([
      rootPost("/near_miss_insights", token, { pharmacyName }),
      rootPost("/near_miss_monthly_summaries", token, { pharmacyName, limit: 36 }),
      rootPost("/near_miss_monthly_report", token, { pharmacyName })
    ]).then(([ins, sums, reps]) => {
      if (cancelled) return;
      if (ins.status === "fulfilled") setInsightsDoc(ins.value);
      if (sums.status === "fulfilled") setSummaries(sums.value?.summaries || []);
      if (reps.status === "fulfilled") setReports(reps.value?.reports || []);
      setBaseLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyName]);

  // Current-month incidents
  useEffect(() => {
    let cancelled = false;
    setCurLoading(true);
    apiGetJson(incidentsUrl(curFrom, new Date()), token)
      .then(d => { if (!cancelled) { setCurData(d); setCurLoading(false); } })
      .catch(() => { if (!cancelled) { setCurData({ recent: [], categories: [] }); setCurLoading(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyName, pharmacyId]);

  // Selected-month incidents (Monthly view)
  useEffect(() => {
    let cancelled = false;
    setMonLoading(true);
    const from = new Date(Date.UTC(selYear, selMonth - 1, 1));
    const to = new Date(Date.UTC(selYear, selMonth, 1));
    apiGetJson(incidentsUrl(from, to), token)
      .then(d => { if (!cancelled) { setMonData(d); setMonLoading(false); } })
      .catch(() => { if (!cancelled) { setMonData({ recent: [], categories: [] }); setMonLoading(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyName, pharmacyId, selYear, selMonth]);

  // Trend series — endpoint needs pharmacyId; fall back to monthly summaries.
  useEffect(() => {
    let cancelled = false;
    if (idLooksReal) {
      rootPost("/near_miss_analysis_trend", token, { pharmacyId, months: 24 })
        .then(j => { if (!cancelled) setTrendSeries(j.series || []); })
        .catch(() => { if (!cancelled) setTrendSeries(null); });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyId]);

  // Month options for the Monthly view selector: last 24 months.
  const monthOptions = useMemo(() => {
    const out = [];
    let y = lastFull.getUTCFullYear(), m = lastFull.getUTCMonth() + 1;
    for (let i = 0; i < 24; i++) {
      out.push({ y, m, label: `${MONTH_NAMES[m - 1]} ${y}` });
      m--; if (m < 1) { m = 12; y--; }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selLabel = `${MONTH_NAMES[selMonth - 1]} ${selYear}`;
  const selSummary = summaries.find(s => Number(s.year) === selYear && Number(s.month) === selMonth) || null;

  // Which month does a manager review belong to? Tolerant on purpose — month
  // may be an int (1-12), a numeric string, or a month name; year may be a
  // number, a string, or missing. Anything unparseable falls back to the
  // createdAt month (with a year adjustment when e.g. a December review was
  // written in January).
  const parseMonthVal = (m) => {
    if (m == null) return null;
    const n = Number(m);
    if (!isNaN(n) && n >= 1 && n <= 12) return n;
    const key = String(m).trim().toLowerCase().slice(0, 3);
    const idx = MONTH_NAMES.findIndex(x => x.toLowerCase().startsWith(key));
    return idx >= 0 ? idx + 1 : null;
  };
  const reportYM = (r) => {
    const m = parseMonthVal(r.month);
    let y = r.year != null ? Number(r.year) : null;
    if (y != null && (isNaN(y) || y < 2000)) y = null;
    if (m && y) return { y, m };
    const d = r.createdAt ? new Date(r.createdAt) : null;
    if (d && !isNaN(d)) {
      if (m) {
        let yy = d.getUTCFullYear();
        if (m > d.getUTCMonth() + 1) yy -= 1; // review written early the following year
        return { y: yy, m };
      }
      return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1 };
    }
    return null;
  };

  const selReviewItems = useMemo(() => {
    const filed = [], written = [];
    for (const r of (reports || [])) {
      const ym = reportYM(r);
      if (ym && ym.y === selYear && ym.m === selMonth) { filed.push({ r, note: null }); continue; }
      const d = r.createdAt ? new Date(r.createdAt) : null;
      if (d && !isNaN(d) && d.getUTCFullYear() === selYear && d.getUTCMonth() + 1 === selMonth) {
        written.push({ r, note: ym ? `written this month · filed under ${MONTH_NAMES[ym.m - 1]} ${ym.y}` : null });
      }
    }
    return [...filed, ...written];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, selYear, selMonth]);
  const selReviews = useMemo(() => selReviewItems.map(x => x.r), [selReviewItems]);

  // Month cards for the "All monthly summaries" tab: engine summaries and
  // manager reviews MERGED by month, so a manager review always appears even
  // when the engine has no summary for that month.
  const monthCards = useMemo(() => {
    const byKey = new Map();
    const keyOf = (y, m) => `${y}-${String(m).padStart(2, "0")}`;
    for (const sum of (summaries || [])) {
      const y = Number(sum.year), m = Number(sum.month);
      if (!y || !m) continue;
      const k = keyOf(y, m);
      if (!byKey.has(k)) byKey.set(k, { y, m, summary: null, reviews: [] });
      byKey.get(k).summary = sum;
    }
    for (const r of (reports || [])) {
      const ym = reportYM(r);
      if (!ym) continue;
      const k = keyOf(ym.y, ym.m);
      if (!byKey.has(k)) byKey.set(k, { y: ym.y, m: ym.m, summary: null, reviews: [] });
      byKey.get(k).reviews.push(r);
    }
    return [...byKey.values()].sort((a, b) => (b.y - a.y) || (b.m - a.m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaries, reports]);

  // Trend bars: prefer the trend endpoint; else derive from engine summaries.
  const trendAll = useMemo(() => {
    if (trendSeries && trendSeries.length) return trendSeries;
    return [...summaries].reverse().map(s => ({
      year: Number(s.year), month: Number(s.month),
      monthLabel: s.monthLabel || `${MONTH_NAMES[(Number(s.month) || 1) - 1]} ${s.year}`,
      totalIncidents: s.totalIncidents || 0,
      totalIncidentsByCategory: s.totalIncidentsByCategory || {}
    }));
  }, [trendSeries, summaries]);

  const trendYears = useMemo(() => {
    const ys = [...new Set(trendAll.map(s => Number(s.year)).filter(Boolean))].sort((a, b) => b - a);
    return ys;
  }, [trendAll]);

  const trendShown = useMemo(() => {
    if (trendYear === "last12") return trendAll.slice(-12);
    return trendAll.filter(s => Number(s.year) === Number(trendYear));
  }, [trendAll, trendYear]);

  const trendOption = useMemo(() => {
    const cats = {};
    for (const s of trendShown) for (const [c, n] of Object.entries(s.totalIncidentsByCategory || {}))
      cats[c] = (cats[c] || 0) + n;
    const topCats = Object.entries(cats).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([c]) => c);
    const x = trendShown.map(s => s.monthLabel || `${s.month}/${s.year}`);
    const series = topCats.length
      ? topCats.map((c, i) => ({
          name: c, type: "bar", stack: "total",
          itemStyle: { color: PIE_PALETTE[i % PIE_PALETTE.length] },
          data: trendShown.map(s => (s.totalIncidentsByCategory || {})[c] || 0)
        }))
      : [{ name: "Incidents", type: "bar",
           itemStyle: { color: "#3b82f6" },
           data: trendShown.map(s => s.totalIncidents || 0),
           label: { show: true, position: "top", fontSize: 10 } }];
    // With categories stacked, add an invisible total label series.
    return {
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      legend: topCats.length ? { bottom: 0, textStyle: { fontSize: 10 } } : undefined,
      grid: { left: 40, right: 16, top: 24, bottom: topCats.length ? 46 : 28 },
      xAxis: { type: "category", data: x, axisLabel: { fontSize: 10, rotate: 30 } },
      yAxis: { type: "value", minInterval: 1 },
      series
    };
  }, [trendShown]);

  const tabBtn = (key, label) => (
    <button key={key} onClick={() => setTab(key)}
      style={{ padding: "8px 16px", fontSize: 13, fontWeight: tab === key ? 700 : 500,
               border: "none", borderBottom: tab === key ? "3px solid #1d4ed8" : "3px solid transparent",
               background: "transparent", color: tab === key ? "#1d4ed8" : "#4b5563", cursor: "pointer" }}>
      {label}
    </button>
  );

  const insights = insightsDoc?.insights || [];
  const rangeTextCur = `${fmtD(curFrom)} → ${fmtD(new Date())}`;
  const selFrom = new Date(Date.UTC(selYear, selMonth - 1, 1));
  const selTo = new Date(Date.UTC(selYear, selMonth, 0));
  const rangeTextSel = `${fmtD(selFrom)} → ${fmtD(selTo)}`;

  const managerReviewBlock = (items) => items.map((item, i) => {
    const r = item.r || item;           // accept {r, note} or a bare report
    const note = item.note || null;
    const ym = reportYM(r);
    const filedLabel = ym ? `${MONTH_NAMES[ym.m - 1]} ${ym.y}` : null;
    const rows = Object.entries(r)
      .filter(([k, v]) => v != null && typeof v !== "object" &&
                          !["_id", "isDeleted", "__v", "year", "month"].includes(k));
    const staff = Array.isArray(r.otherStaffNames) ? r.otherStaffNames.filter(Boolean).join(", ") : null;
    return (
      <div key={i} style={{ background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 6,
                            padding: 10, marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 12, color: "#1e3a8a", marginBottom: 4 }}>
          Manager monthly review{filedLabel ? ` — ${filedLabel}` : (r.createdAt ? ` — ${fmtD(r.createdAt)}` : "")}
          {note && (
            <span style={{ fontWeight: 400, fontSize: 11, color: "#1d4ed8" }}> ({note})</span>
          )}
        </div>
        <table style={{ fontSize: 12, color: "#1e293b", borderCollapse: "collapse" }}>
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k}>
                <td style={{ paddingRight: 8, fontWeight: 600, verticalAlign: "top", whiteSpace: "nowrap" }}>
                  {k.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase())}
                </td>
                <td>{String(v)}</td>
              </tr>
            ))}
            {staff && <tr><td style={{ paddingRight: 8, fontWeight: 600 }}>Other staff</td><td>{staff}</td></tr>}
          </tbody>
        </table>
      </div>
    );
  });

  return (
    <div onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1300,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()}
           style={{ width: "min(1280px, 96vw)", height: "min(860px, 92vh)", background: "#fff",
                    borderRadius: 10, boxShadow: "0 16px 60px rgba(0,0,0,0.35)",
                    display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "12px 18px 0 18px" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#0f172a" }}>
              Near miss — {pharmacyName}
            </div>
            {insightsDoc?.computedAt && (
              <div style={{ fontSize: 11, color: "#6b7280" }}>
                Insights computed {fmtD(insightsDoc.computedAt)} · window {fmtD(insightsDoc.windowStart)} → {fmtD(insightsDoc.windowEnd)}
                {insightsDoc.estimated ? " · rates estimated" : ""}
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="Close"
                  style={{ border: "1px solid #d1d5db", background: "#fff", borderRadius: 6,
                           padding: "5px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700,
                           color: "#374151", lineHeight: 1.2, display: "inline-flex",
                           alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 15, color: "#111827" }}>×</span> Close
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, borderBottom: "1px solid #e5e7eb", padding: "6px 12px 0 12px" }}>
          {tabBtn("current", "Current month")}
          {tabBtn("monthly", "Monthly view")}
          {tabBtn("summaries", "All monthly summaries")}
          {tabBtn("trends", "Trends")}
        </div>

        {/* Body */}
        <div style={{ flex: 1, minHeight: 0, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>

          {/* ---------------- Current month ---------------- */}
          {tab === "current" && (
            <>
              <div style={{ background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 6,
                            padding: "8px 12px", fontSize: 13, color: "#3730a3" }}>
                <b>Current month</b> — showing activity from {rangeTextCur}
                {" "}({MONTH_NAMES[now.getUTCMonth()]} {now.getUTCFullYear()}, month to date
                {curData ? ` · ${(curData.recent || []).length} incident${(curData.recent || []).length === 1 ? "" : "s"}` : ""}).
              </div>
              {curLoading || baseLoading ? (
                <div style={{ color: "#6b7280", fontSize: 13 }}>Loading…</div>
              ) : (
                <ThreePanel
                  left={
                    <PanelBox title={`Insights (${insights.length})`}>
                      {insights.length === 0
                        ? <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {insightsDoc?.meta?.message || "No insights computed yet for this branch."}
                          </div>
                        : insights.map((ins, i) => <InsightItem key={i} ins={ins} />)}
                    </PanelBox>
                  }
                  categories={curData?.categories || []}
                  pieTitle={`Near misses — ${pharmacyName}`}
                  incidents={curData?.recent || []}
                />
              )}
            </>
          )}

          {/* ---------------- Monthly view ---------------- */}
          {tab === "monthly" && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Month:</label>
                <select value={`${selYear}-${selMonth}`}
                        onChange={e => { const [y, m] = e.target.value.split("-").map(Number); setSelYear(y); setSelMonth(m); }}
                        style={{ padding: "6px 10px", fontSize: 13, border: "1px solid #d1d5db", borderRadius: 6 }}>
                  {monthOptions.map(o => (
                    <option key={`${o.y}-${o.m}`} value={`${o.y}-${o.m}`}>{o.label}</option>
                  ))}
                </select>
                <div style={{ flex: 1 }} />
                <button onClick={() => exportMonthPdf({
                          pharmacyName, monthLabel: selLabel, rangeText: rangeTextSel,
                          summary: selSummary, managerReviews: selReviews,
                          insights: selSummary?.insights?.length ? selSummary.insights : insights,
                          incidents: monData?.recent || [],
                          categories: monData?.categories || [] })}
                        style={{ padding: "6px 14px", fontSize: 12.5, fontWeight: 700,
                                 border: "1px solid #9ca3af", borderRadius: 6, background: "#fff",
                                 color: "#374151", lineHeight: 1.2, cursor: "pointer" }}>
                  Export to PDF
                </button>
                <button onClick={() => exportMonthExcel({
                          pharmacyName, monthLabel: selLabel,
                          summary: selSummary, incidents: monData?.recent || [],
                          categories: monData?.categories || [],
                          insights: selSummary?.insights?.length ? selSummary.insights : insights,
                          managerReviews: selReviews })}
                        style={{ padding: "6px 14px", fontSize: 12.5, fontWeight: 700,
                                 border: "1px solid #9ca3af", borderRadius: 6, background: "#fff",
                                 color: "#374151", lineHeight: 1.2, cursor: "pointer" }}>
                  Export to Excel
                </button>
              </div>
              <div style={{ background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 6,
                            padding: "8px 12px", fontSize: 13, color: "#3730a3" }}>
                <b>{selLabel}</b> — showing activity from {rangeTextSel}
                {monData ? ` · ${(monData.recent || []).length} incident${(monData.recent || []).length === 1 ? "" : "s"}` : ""}.
              </div>
              {monLoading ? (
                <div style={{ color: "#6b7280", fontSize: 13 }}>Loading…</div>
              ) : (
                <ThreePanel
                  left={
                    <PanelBox title="Insights & monthly review">
                      {/* Manager's own monthly summary first, when one exists */}
                      {managerReviewBlock(selReviewItems)}
                      {selSummary && (
                        <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 6,
                                      padding: 10, marginBottom: 8 }}>
                          <div style={{ fontWeight: 700, fontSize: 12, color: "#78350f", marginBottom: 4 }}>
                            {selSummary.monthLabel || selLabel} — engine summary
                            <span style={{ fontWeight: 400, color: "#92400e" }}>
                              {" "}· {selSummary.totalIncidents} incident{selSummary.totalIncidents === 1 ? "" : "s"}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: "#451a03", lineHeight: 1.5 }}>{selSummary.narrative}</div>
                        </div>
                      )}
                      {(selSummary?.insights?.length ? selSummary.insights : []).map((ins, i) =>
                        <InsightItem key={i} ins={ins} />)}
                      {!selSummary && selReviewItems.length === 0 && (
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                          No engine summary or manager review for {selLabel} yet.
                        </div>
                      )}
                    </PanelBox>
                  }
                  categories={monData?.categories || []}
                  pieTitle={`Near misses — ${selLabel}`}
                  incidents={monData?.recent || []}
                />
              )}
            </>
          )}

          {/* ---------------- All monthly summaries ---------------- */}
          {tab === "summaries" && (
            <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <PanelBox title={`Monthly near miss review (${monthCards.length} months)`} style={{ flex: 1 }}>
                {baseLoading ? <div style={{ fontSize: 12, color: "#6b7280" }}>Loading…</div>
                 : monthCards.length === 0 ? <div style={{ fontSize: 12, color: "#6b7280" }}>No monthly summaries yet.</div>
                 : monthCards.map((mc, i) => {
                    const sum = mc.summary;
                    const label = sum?.monthLabel || `${MONTH_NAMES[mc.m - 1]} ${mc.y}`;
                    return (
                      <div key={i} style={{ background: sum ? "#fef3c7" : "#eff6ff",
                                            border: sum ? "1px solid #fbbf24" : "1px solid #93c5fd",
                                            borderRadius: 8, padding: 10, marginBottom: 8, cursor: "pointer" }}
                           title="Open this month in the Monthly view tab"
                           onClick={() => { setSelYear(mc.y); setSelMonth(mc.m); setTab("monthly"); }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                          <span style={{ fontWeight: 700, fontSize: 12.5, color: sum ? "#78350f" : "#1e3a8a" }}>
                            {label} — {sum ? "engine summary" : "manager review"}
                          </span>
                          <span style={{ fontSize: 11, color: sum ? "#92400e" : "#1d4ed8" }}>
                            {sum ? `${sum.totalIncidents} incident${sum.totalIncidents === 1 ? "" : "s"}` : ""}
                            {sum?.insights?.length ? ` · ${sum.insights.length} pattern${sum.insights.length === 1 ? "" : "s"}` : ""}
                            {mc.reviews.length ? `${sum ? " · " : ""}manager review ✓` : ""}
                          </span>
                        </div>
                        {sum && (
                          <div style={{ fontSize: 12, color: "#451a03", marginTop: 4, lineHeight: 1.5 }}>{sum.narrative}</div>
                        )}
                        {sum?.insights?.length > 0 && (
                          <ul style={{ fontSize: 11.5, color: "#451a03", margin: "6px 0 0", paddingLeft: 16, lineHeight: 1.5 }}>
                            {sum.insights.map((ins, j) => (
                              <li key={j}><b>{ins.title}</b> ({ins.id}) — {ins.detail}</li>
                            ))}
                          </ul>
                        )}
                        {mc.reviews.length > 0 && (
                          <div style={{ marginTop: sum ? 8 : 4 }} onClick={e => e.stopPropagation()}>
                            {managerReviewBlock(mc.reviews)}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </PanelBox>
            </div>
          )}

          {/* ---------------- Trends ---------------- */}
          {tab === "trends" && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Period:</label>
                <select value={String(trendYear)}
                        onChange={e => setTrendYear(e.target.value === "last12" ? "last12" : Number(e.target.value))}
                        style={{ padding: "6px 10px", fontSize: 13, border: "1px solid #d1d5db", borderRadius: 6 }}>
                  <option value="last12">Last 12 months</option>
                  {trendYears.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                {!idLooksReal && (
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>
                    (derived from monthly engine summaries)
                  </span>
                )}
              </div>
              <div style={{ flex: 1, minHeight: 0, border: "1px solid #e5e7eb", borderRadius: 8, padding: 8 }}>
                {trendShown.length === 0
                  ? <div style={{ fontSize: 12, color: "#6b7280", padding: 12 }}>No trend data for this period.</div>
                  : <ReactECharts option={trendOption} style={{ height: "100%", width: "100%" }} notMerge />}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}